/**
 * @file Toast.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Toast notification component for the Shay Suite Admin application.
 *              This file provides a reusable toast notification component using
 *              Material-UI Snackbar and Alert components. It supports different
 *              severity levels and customizable display duration.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

/**
 * Props interface for the Toast component
 */
interface ToastProps {
  open: boolean;                    // Whether the toast is visible
  message: string;                  // Message to display in the toast
  severity: AlertColor;             // Severity level (success, error, warning, info)
  onClose: () => void;             // Function called when toast is closed
  autoHideDuration?: number;        // Duration in milliseconds before auto-hide (default: 6000)
}

/**
 * Toast notification component
 * 
 * This component:
 * - Displays temporary notification messages to users
 * - Supports different severity levels with appropriate styling
 * - Auto-hides after a configurable duration
 * - Can be manually dismissed by the user
 * - Uses Material-UI components for consistent styling
 * - Positions notifications in the top-right corner
 * 
 * @param {ToastProps} props - Component props for toast configuration
 * @returns {JSX.Element} Toast notification component
 */
const Toast: React.FC<ToastProps> = ({
  open,
  message,
  severity,
  onClose,
  autoHideDuration = 6000,
}) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast; 